import React, { useEffect, useState } from 'react';
import { FaUserCircle } from 'react-icons/fa';
import { IoMdAdd } from 'react-icons/io';
import { MdKeyboardArrowDown } from 'react-icons/md';
import { AiFillCodepenCircle } from 'react-icons/ai';
import { FaChevronDown, FaChevronUp, FaRocket } from 'react-icons/fa';
import { IoLink, IoClose } from 'react-icons/io5';
import { toast } from 'react-hot-toast';
import { BiSolidEdit } from 'react-icons/bi';
import { HiLightningBolt } from 'react-icons/hi';

import JiraIssueActivity from './JiraIssueActivity';
import {
  getJiraIssueDetails,
  updateJiraIssue,
  fetchJiraUsers,
  fetchJiraIssues,
} from '../../services/api';
import { DataState } from '../../context/DataProvider';
import 'react-quill/dist/quill.snow.css';
import { sanitizeHtml } from '../../utils/sanitize';

const SkeletonLoader = ({ type = 'text' }) => {
  if (type === 'text') {
    return (
      <div className="mr-4 space-y-4">
        <div className="h-6 w-full animate-pulse rounded bg-gray-300"></div>
        <div className="h-6 w-3/4 animate-pulse rounded bg-gray-300"></div>
        <div className="h-6 w-1/2 animate-pulse rounded bg-gray-300"></div>
      </div>
    );
  }
  if (type === 'description') {
    return (
      <div className="mr-4 space-y-4">
        <div className="h-3 w-full animate-pulse rounded bg-gray-300"></div>
        <div className="h-3 w-3/4 animate-pulse rounded bg-gray-300"></div>
        <div className="h-3 w-1/2 animate-pulse rounded bg-gray-300"></div>
        <div className="h-3 w-3/4 animate-pulse rounded bg-gray-300"></div>
        <div className="h-3 w-1/2 animate-pulse rounded bg-gray-300"></div>
      </div>
    );
  }

  if (type === 'image') {
    return (
      <div className="mr-4 h-48 w-full animate-pulse rounded-lg bg-gray-300"></div>
    );
  }

  if (type === 'card') {
    return (
      <div className="mr-4 w-full max-w-sm space-y-4 rounded-lg bg-white p-4 shadow-md">
        <div className="h-32 w-full animate-pulse rounded-lg bg-gray-300"></div>
        <div className="h-6 animate-pulse rounded bg-gray-300"></div>
        <div className="h-6 w-3/4 animate-pulse rounded bg-gray-300"></div>
      </div>
    );
  }

  return null;
};

function convertToIST(dateString) {
  // Parse the date string into a Date object (in UTC)
  const dateUTC = new Date(dateString);

  // Format the date to "28 Jan, 2025"
  const formattedDate = dateUTC.toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });

  return formattedDate;
}

const JiraIssueModal = ({ issue_key, onClose }) => {
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [isAutomationOpen, setIsAutomationOpen] = useState(false);
  const [issue, setIssue] = useState({});
  const { userGoogleId } = DataState();

  const [isEditing, setIsEditing] = useState(false);
  const [isAssigneeEditing, setIsAssigneeEditing] = useState(false);
  const [updatedIssue, setUpdatedIssue] = useState(issue);
  const [allAssignees, setAllAssignees] = useState([]);
  const [assigneeQuery, setAssigneeQuery] = useState('');
  const [assigneeOptions, setAssigneeOptions] = useState([]);
  const [selectedAssignee, setSelectedAssignee] = useState(
    issue?.fields?.assignee || null,
  );
  const [loading, setLoading] = useState(true);
  const [saveLoading, setSaveLoading] = useState(false);

  const toggleDetails = () => setIsDetailsOpen(!isDetailsOpen);
  const toggleAutomation = () => setIsAutomationOpen(!isAutomationOpen);
  const [avatarUrl, setAvatarUrl] = useState(null);
  const [issuesforparentkey, setIssuesForParentKey] = useState([]);
  const [filteredIssues, setFilteredIssues] = useState([]);

  const [selectedParentKey, setSelectedParentKey] = useState(
    updatedIssue?.fields?.parent?.key || '',
  );
  const [showDropdown, setShowDropdown] = useState(false);

  useEffect(() => {
    const assigneeId = selectedAssignee
      ? selectedAssignee.accountId
      : issue?.fields?.assignee?.accountId;

    if (assigneeId) {
      const assignee = allAssignees.find(
        (user) => user.accountId === assigneeId,
      );
      setAvatarUrl(assignee ? assignee.avatarUrls['16x16'] : null);
    } else {
      setAvatarUrl(null);
    }
  }, [selectedAssignee, issue, allAssignees]);

  const setIssueDetails = async () => {
    if (!issue_key) {
      return;
    }

    try {
      let message_data = null;

      message_data = await getJiraIssueDetails(issue_key, userGoogleId);

      setIssue(message_data);
      setLoading(false);
    } catch (error) {
      toast.error(error);
      setLoading(false);
    }
  };

  useEffect(() => {
    setIssueDetails();
    // eslint-disable-next-line
  }, [issue_key]);

  useEffect(() => {
    const getIssues = async () => {
      try {
        const issues = await fetchJiraIssues('KAN', userGoogleId);
        setIssuesForParentKey(issues);
      } catch (error) {
        toast.error('Failed to fetch Jira Issues.');
      }
    };
    getIssues();

    // eslint-disable-next-line
  }, [issue_key]);
  useEffect(() => {}, [issuesforparentkey]);

  useEffect(() => {
    setUpdatedIssue(issue);
  }, [issue]);

  const visitIssue = (issueLink) => {
    // const issueLink = "https://example.com";
    window.open(issueLink, '_blank');
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    setUpdatedIssue((prev) => ({
      ...prev,
      fields: {
        ...prev.fields,

        // Ensure status exists before updating status.name
        status: {
          ...prev.fields?.status, // Keep existing status properties
          name: name === 'status' ? value : prev.fields?.status?.name, // Only update name
        },

        // Update other fields normally
        ...(name !== 'status' && { [name]: value }),
      },
    }));
  };

  useEffect(() => {
    if (selectedParentKey.trim()) {
      const filtered = issuesforparentkey.filter(
        (i) =>
          i.issuetype.toLowerCase() === 'epic' && // Exclude Epics from selection
          i.key.toLowerCase().includes(selectedParentKey.toLowerCase()), // Match typed input
      );
      setFilteredIssues(filtered);
    } else {
      setFilteredIssues([]);
    }
  }, [selectedParentKey, issuesforparentkey]);

  const handleUpdateIssue = async () => {
    setSaveLoading(true);
    try {
      const updatedFields = {
        summary: updatedIssue?.fields?.summary,
        description: updatedIssue?.fields?.description,
        issue_type: updatedIssue?.fields?.issuetype?.name,
        assignee_id: updatedIssue?.fields?.assignee?.accountId,
        duedate: updatedIssue?.fields?.duedate,
        parent_issue_key: updatedIssue?.fields?.parent?.key,
        status: updatedIssue?.fields?.status?.name,
      };

      await updateJiraIssue(issue_key, updatedFields, userGoogleId);
      toast.success('Issue updated successfully!');

      setIsEditing(false);
      setIssue(updatedIssue);
    } catch (error) {
      toast.error('Failed to update the issue.');
    } finally {
      setSaveLoading(false);
    }
  };

  useEffect(() => {
    const fetchAllAssignees = async () => {
      try {
        const data = await fetchJiraUsers('', userGoogleId); // Fetch all users without filtering
        setAllAssignees(data.users || []);
      } catch (error) {
        toast.error('Failed to fetch assignees.');
      }
    };

    fetchAllAssignees();
  }, [userGoogleId]);

  useEffect(() => {
    if (assigneeQuery.length >= 1) {
      const filteredUsers = allAssignees.filter((user) =>
        user.displayName.toLowerCase().includes(assigneeQuery.toLowerCase()),
      );
      setAssigneeOptions(filteredUsers);
    } else {
      setAssigneeOptions([]);
    }
  }, [assigneeQuery, allAssignees]);

  const handleSelectAssignee = (user) => {
    setSelectedAssignee(user);
    setAssigneeQuery(user.displayName);
    setAssigneeOptions([]);
    setIsAssigneeEditing(false);

    setUpdatedIssue((prev) => ({
      ...prev,
      fields: {
        ...prev.fields,
        assignee: {
          ...prev.fields?.assignee, // Preserve other assignee fields
          accountId: user.accountId, // Update the selected assignee ID
        },
      },
    }));
  };

  const stripHtmlTags = (html) => {
    const div = document.createElement('div');
    div.innerHTML = html;
    return div.textContent || div.innerText || '';
  };

  return (
    // eslint-disable-next-line
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm"
      onClick={onClose}
    >
      {/* eslint-disable-next-line */}
      <div
        className="flex w-full flex-col overflow-hidden bg-white p-6 shadow-xl max-sm:h-full sm:w-[65%] sm:rounded-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex w-full items-center justify-between sm:pl-4 sm:pr-4">
          <div className="mb-2 flex items-center text-sm text-gray-600">
            {loading ? (
              <div className="h-4 w-96 animate-pulse rounded bg-gray-300" />
            ) : (
              <>
                {issue?.fields?.parent && (
                  <>
                    <img
                      key={issue?.fields?.parent?.fields?.issuetype?.name}
                      src={`/assets/jiraissuetable/jiraIssueTypeIcons/${issue?.fields?.parent.fields?.issuetype?.name.toLowerCase()}.png`}
                      alt={`${issue?.fields?.parent.fields?.issuetype?.name} Icon`}
                      className="mr-1 h-4 w-4"
                      onError={(e) => (e.target.style.display = 'none')}
                    />
                    {issue?.fields?.parent?.key} /
                  </>
                )}

                <img
                  key={issue?.fields?.issuetype?.name}
                  src={`/assets/jiraissuetable/jiraIssueTypeIcons/${issue?.fields?.issuetype?.name.toLowerCase()}.png`}
                  alt={`${issue?.fields?.issuetype?.name} Icon`}
                  className="mx-1 h-4 w-4"
                  onError={(e) => (e.target.style.display = 'none')}
                />

                {issue?.key}
              </>
            )}
          </div>
          <div className="flex items-center justify-center gap-4">
            {!isEditing && (
              <div>
                <button
                  className="text-gray-500 hover:text-gray-800"
                  onClick={() => {
                    setIsEditing(!isEditing);
                    setIsAssigneeEditing(true);
                  }}
                >
                  <BiSolidEdit size={20} />
                </button>
              </div>
            )}
            <div>
              <button
                onClick={() => visitIssue(issue?.fields?.issue_url)}
                className="text-gray-500 hover:text-gray-800"
              >
                <IoLink size={20} />
              </button>
            </div>
            <div>
              <button
                onClick={onClose}
                className="text-gray-500 hover:text-gray-800"
              >
                <IoClose size={20} />
              </button>
            </div>
          </div>
        </div>
        <div className="flex gap-4">
          <div className="w-full max-sm:pt-4 sm:w-[60%] sm:p-4 sm:pr-0">
            {/* Header */}
            <div className="mb-4 flex flex-col items-center justify-between border-b pb-4">
              <div className="flex w-full text-xl font-semibold text-gray-800">
                {loading ? (
                  <div className="h-6 w-full animate-pulse rounded bg-gray-300" />
                ) : isEditing ? (
                  <input
                    type="text"
                    name="summary"
                    value={updatedIssue?.fields?.summary || ''}
                    onChange={handleInputChange}
                    className="w-full rounded-md border border-gray-300 p-2 text-xl font-semibold  text-gray-800 focus:outline-none focus:ring-0"
                  />
                ) : (
                  <span>{issue?.fields?.summary}</span>
                )}
              </div>
            </div>

            {/* Details */}
            <div className="mb-6 flex gap-1">
              {loading ? (
                <>
                  <div className="h-8 w-24 animate-pulse rounded-md bg-gray-300" />
                  <div className="h-8 w-24 animate-pulse rounded-md bg-gray-300" />
                  <div className="h-8 w-24 animate-pulse rounded-md bg-gray-300" />
                </>
              ) : isEditing ? (
                <>
                  <select
                    name="status"
                    value={updatedIssue?.fields?.status?.name || ''}
                    onChange={handleInputChange}
                    className="flex items-center rounded-md bg-gray-200 px-3 py-1 text-sm text-gray-600"
                  >
                    <option value="To Do">To Do</option>
                    <option value="In Progress">In Progress</option>
                    <option value="Done">Done</option>
                  </select>
                  <button className="flex items-center rounded-md bg-gray-200 px-3 py-1 text-sm text-gray-600">
                    <IoMdAdd className="mr-1" /> Add
                  </button>
                  <button className="flex items-center rounded-md bg-gray-200 px-3 py-1 text-sm text-gray-600">
                    <AiFillCodepenCircle className="mr-1" /> Apps
                  </button>
                </>
              ) : (
                <>
                  <button className="flex items-center rounded-md bg-gray-200 px-3 py-1 text-sm text-gray-600">
                    {issue?.fields?.status?.name}{' '}
                    <MdKeyboardArrowDown className="ml-1" />
                  </button>
                  <button className="flex items-center rounded-md bg-gray-200 px-3 py-1 text-sm text-gray-600">
                    <IoMdAdd className="mr-1" /> Add
                  </button>
                  <button className="flex items-center rounded-md bg-gray-200 px-3 py-1 text-sm text-gray-600">
                    <AiFillCodepenCircle className="mr-1" /> Apps
                  </button>
                </>
              )}
            </div>
            <div className="hide-sidebar max-h-[600px] flex-1 overflow-y-auto sm:max-h-[400px]">
              <div className="overflow-hidden">
                {/* Description */}
                <div className="mb-6 mr-2">
                  <h3 className="text-sm font-semibold text-gray-700">
                    Description
                  </h3>
                  <div className="mt-2 whitespace-pre-line text-sm text-gray-600">
                    {loading ? (
                      <SkeletonLoader type="description" />
                    ) : isEditing ? (
                      // <ReactQuill
                      //   value={updatedIssue?.fields?.description || ""}
                      //   onChange={(content) => handleQuillChange(content, "description")}
                      //   modules={modules}
                      //   formats={formats}
                      //   className="w-full text-gray-600 text-md"
                      // />
                      <textarea
                        id="textarea"
                        rows="6"
                        value={stripHtmlTags(
                          updatedIssue?.fields?.description || '',
                        )}
                        name="description"
                        onChange={handleInputChange}
                        className="hide-sidebar w-full resize-none rounded-lg border border-gray-300 p-3 text-sm text-gray-800 focus:border-gray-400 focus:outline-none focus:ring-1 focus:ring-gray-400"
                      ></textarea>
                    ) : issue?.fields?.description ? (
                      <div
                        dangerouslySetInnerHTML={{
                          __html: sanitizeHtml(issue.fields.description),
                        }}
                      />
                    ) : (
                      <em className="italic">No description</em>
                    )}
                  </div>
                </div>

                <div className="block w-full rounded-lg bg-white pt-4 sm:hidden">
                  {/* Actions Section */}
                  <div className="mb-2 flex items-center">
                    <h3 className="flex items-center justify-center font-semibold text-gray-800">
                      <HiLightningBolt /> Actions
                    </h3>
                    <button className="ml-1 text-gray-500">
                      <FaChevronDown />
                    </button>
                  </div>

                  {/* Details Section */}
                  <div>
                    {/* eslint-disable-next-line */}
                    <div
                      className="flex cursor-pointer items-center justify-between border border-gray-300 px-4 py-2"
                      onClick={toggleDetails}
                    >
                      <h4 className="font-medium text-gray-700">Details</h4>
                      <button className="text-gray-500">
                        {isDetailsOpen ? <FaChevronUp /> : <FaChevronDown />}
                      </button>
                    </div>
                    {isDetailsOpen && (
                      <div className="space-y-2 border border-t-0 border-gray-300 px-4 py-2 text-sm text-gray-700 ">
                        <div className="grid grid-cols-2 items-center py-2 ">
                          <div className="flex items-center">
                            <span className="ml-2">Assignee</span>
                          </div>
                          <div>
                            {isEditing && isAssigneeEditing ? (
                              <div className="relative">
                                <input
                                  type="text"
                                  value={assigneeQuery}
                                  onChange={(e) =>
                                    setAssigneeQuery(e.target.value)
                                  }
                                  onBlur={() =>
                                    setTimeout(
                                      () => setAssigneeOptions([]),
                                      200,
                                    )
                                  }
                                  placeholder="Search assignee..."
                                  className="w-full resize-none rounded border p-2 focus:outline-none"
                                />
                                {assigneeOptions.length > 0 && (
                                  <ul className="scrollbar-thin scrollbar-track-gray-200 scrollbar-thumb-gray-500 absolute z-10 max-h-40 w-full overflow-y-auto rounded border bg-white shadow-md">
                                    {assigneeOptions.map((user) => (
                                      // eslint-disable-next-line
                                      <li
                                        key={user.accountId}
                                        className="flex cursor-pointer items-center p-2 text-sm hover:bg-gray-200"
                                        onClick={() =>
                                          handleSelectAssignee(user)
                                        }
                                      >
                                        <img
                                          src={user.avatarUrls['16x16']}
                                          alt={user.displayName}
                                          className="w- mr-2 h-7 rounded-full"
                                        />
                                        {user.displayName}
                                      </li>
                                    ))}
                                  </ul>
                                )}
                              </div>
                            ) : (
                              <div>
                                {selectedAssignee || issue?.fields?.assignee ? (
                                  // eslint-disable-next-line
                                  <div
                                    onClick={() => setIsAssigneeEditing(true)}
                                    className="flex cursor-pointer items-center"
                                  >
                                    {avatarUrl && (
                                      <img
                                        src={avatarUrl}
                                        alt={
                                          (
                                            selectedAssignee ||
                                            issue.fields.assignee
                                          ).displayName
                                        }
                                        className="mr-2 h-7 w-7 rounded-full"
                                      />
                                    )}
                                    {
                                      (
                                        selectedAssignee ||
                                        issue.fields.assignee
                                      ).displayName
                                    }
                                  </div>
                                ) : (
                                  <span className="italic text-gray-500">
                                    Unassigned
                                  </span>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="grid grid-cols-2 items-center py-2 ">
                          <div className="flex items-center">
                            <span className="ml-2">Labels</span>
                          </div>
                          <div className="flex flex-wrap gap-2">
                            {issue?.fields?.labels &&
                            issue?.fields?.labels.length > 0 ? (
                              issue?.fields?.labels.map((label) => (
                                <span
                                  key={label}
                                  className="rounded-lg bg-zinc-300  px-2 py-1 text-xs font-medium text-gray-600"
                                >
                                  {label}
                                </span>
                              ))
                            ) : (
                              <span className="rounded-lg bg-gray-400 px-3 py-1 text-sm font-medium text-white">
                                None
                              </span>
                            )}
                          </div>
                        </div>
                        {!(
                          issue?.fields?.issuetype?.name?.toLowerCase() ===
                          'epic'
                        ) && (
                          <div className="grid grid-cols-2 items-center py-2 ">
                            <div className="flex items-center">
                              <span className="ml-2">Parent</span>
                            </div>
                            {loading ? (
                              <div className="h-6 w-full animate-pulse rounded bg-gray-300" />
                            ) : isEditing ? (
                              <div className="relative w-full">
                                <input
                                  type="text"
                                  name="parentKey"
                                  value={selectedParentKey}
                                  onChange={(e) => {
                                    setSelectedParentKey(e.target.value);
                                    setShowDropdown(true);
                                  }}
                                  autoComplete="off"
                                  onFocus={() => setShowDropdown(true)}
                                  onBlur={() =>
                                    setTimeout(
                                      () => setShowDropdown(false),
                                      200,
                                    )
                                  }
                                  className="w-full rounded-md border border-gray-300 p-1 text-sm font-semibold text-gray-800 focus:outline-none focus:ring-0"
                                />

                                {showDropdown && filteredIssues.length > 0 && (
                                  <ul className="scrollbar-thin scrollbar-track-gray-200 scrollbar-thumb-gray-500 absolute left-0 z-10 max-h-40 w-full overflow-y-auto rounded border bg-white shadow-md ">
                                    {filteredIssues.map((issueItem) => (
                                      // eslint-disable-next-line
                                      <li
                                        key={issueItem.key}
                                        className="flex cursor-pointer items-center p-2 text-sm hover:bg-gray-200"
                                        onMouseDown={(e) => {
                                          setSelectedParentKey(issueItem.key);
                                          setShowDropdown(false);

                                          setUpdatedIssue((prevIssue) => ({
                                            ...prevIssue,
                                            fields: {
                                              ...prevIssue.fields,
                                              parent: {
                                                ...prevIssue.fields?.parent,
                                                key: issueItem.key, // Update the parent key inside fields
                                              },
                                            },
                                          }));
                                        }}
                                      >
                                        {
                                          <img
                                            key={issueItem?.issuetype}
                                            src={`/assets/jiraissuetable/jiraIssueTypeIcons/${issueItem?.issuetype?.toLowerCase()}.png`}
                                            alt={`${issueItem?.issuetype} Icon`}
                                            className="mr-1 h-4 w-4"
                                            onError={(e) =>
                                              (e.target.style.display = 'none')
                                            }
                                          />
                                        }
                                        {issueItem.key}
                                      </li>
                                    ))}
                                  </ul>
                                )}
                              </div>
                            ) : (
                              <>
                                <div>
                                  {issue?.fields?.parent?.key || (
                                    <span className="italic text-gray-500">
                                      None
                                    </span>
                                  )}
                                </div>
                              </>
                            )}
                          </div>
                        )}

                        {/* Due Date */}
                        <div className="grid grid-cols-2 items-center py-2">
                          <div className="flex items-center">
                            <span className="ml-2">Due Date</span>
                          </div>
                          <div>
                            {isEditing ? (
                              <input
                                type="date"
                                name="duedate"
                                value={updatedIssue?.fields?.duedate || ''}
                                onChange={handleInputChange}
                                className="rounded-md border border-gray-300 p-2 focus:outline-none focus:ring-0"
                              />
                            ) : issue?.fields?.duedate ? (
                              convertToIST(issue.fields.duedate)
                            ) : (
                              <span className="italic text-gray-500">None</span>
                            )}
                          </div>
                        </div>

                        {/* Reporter */}
                        <div className="grid grid-cols-2 items-center py-2 ">
                          <div className="flex items-center">
                            <span className="ml-2">Reporter</span>
                          </div>
                          <div>
                            {issue.fields?.reporter ? (
                              <div className="flex items-center">
                                <FaUserCircle className="mr-2 text-green-500" />
                                {issue.fields?.reporter?.displayName}
                              </div>
                            ) : (
                              <span className="italic text-gray-500">
                                No Reporter
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Automation Section */}
                  <div className="mt-4">
                    {/* eslint-disable-next-line */}
                    <div
                      className="flex cursor-pointer items-center justify-between border border-gray-300 px-4 py-2"
                      onClick={toggleAutomation}
                    >
                      <h4 className="font-medium text-gray-700">Automation</h4>
                      <button className="text-gray-500">
                        {isAutomationOpen ? <FaChevronUp /> : <FaChevronDown />}
                      </button>
                    </div>
                    {isAutomationOpen && (
                      <div className="space-y-2 border  border-t-0 border-gray-300 px-4 py-2 text-gray-700">
                        <div className="flex items-center">
                          <FaRocket className="text-gray-500" />
                          <span className="ml-2">Rule executions</span>
                        </div>
                        {/* Add more dummy fields here */}
                        <div className="flex items-center">
                          <FaRocket className="text-gray-500" />
                          <span className="ml-2">Dummy field 1</span>
                        </div>
                        <div className="flex items-center">
                          <FaRocket className="text-gray-500" />
                          <span className="ml-2">Dummy field 2</span>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Created and Updated Section */}
                  <div className="mt-4 text-sm text-gray-600">
                    <p>
                      Created{' '}
                      {new Date(issue?.fields?.created).toLocaleString()}
                    </p>
                    <p>
                      Updated{' '}
                      {new Date(issue?.fields?.updated).toLocaleString()}
                    </p>
                  </div>
                </div>

                {/* Activity / comments */}
                <div className="mb-6 mr-2">
                  <JiraIssueActivity
                    issue_key={issue_key}
                    comments={issue?.fields?.comment?.comments}
                    allAssignees={allAssignees}
                    refreshIssue={setIssueDetails}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="hidden w-[40%] rounded-lg bg-white p-4 sm:block ">
            {/* Actions Section */}
            <div className="mb-2 flex items-center">
              <h3 className="flex items-center justify-center font-semibold text-gray-800">
                <HiLightningBolt /> Actions
              </h3>
              <button className="ml-1 text-gray-500">
                <FaChevronDown />
              </button>
            </div>

            {/* Details Section */}
            <div>
              {/* eslint-disable-next-line */}
              <div
                className="flex cursor-pointer items-center justify-between border border-gray-300 px-4 py-2"
                onClick={toggleDetails}
              >
                <h4 className="font-medium text-gray-700">Details</h4>
                <button className="text-gray-500">
                  {isDetailsOpen ? <FaChevronUp /> : <FaChevronDown />}
                </button>
              </div>
              {isDetailsOpen && (
                <div className="space-y-2 border border-t-0 border-gray-300 px-4 py-2 text-sm text-gray-700 ">
                  <div className="grid grid-cols-2 items-center py-2 ">
                    <div className="flex items-center">
                      <span className="ml-2">Assignee</span>
                    </div>
                    <div>
                      {isEditing && isAssigneeEditing ? (
                        <div className="relative">
                          <input
                            type="text"
                            value={assigneeQuery}
                            onChange={(e) => setAssigneeQuery(e.target.value)}
                            onBlur={() =>
                              setTimeout(() => setAssigneeOptions([]), 200)
                            }
                            placeholder="Search assignee..."
                            className="w-full resize-none rounded border p-2 focus:outline-none"
                          />
                          {assigneeOptions.length > 0 && (
                            <ul className="scrollbar-thin scrollbar-track-gray-200 scrollbar-thumb-gray-500 absolute z-10 max-h-40 w-full overflow-y-auto rounded border bg-white shadow-md">
                              {assigneeOptions.map((user) => (
                                // eslint-disable-next-line
                                <li
                                  // eslint-disable-next-line
                                  key={user.accountId}
                                  className="flex cursor-pointer items-center p-2 text-sm hover:bg-gray-200"
                                  onClick={() => handleSelectAssignee(user)}
                                >
                                  <img
                                    src={user.avatarUrls['16x16']}
                                    alt={user.displayName}
                                    className="w- mr-2 h-7 rounded-full"
                                  />
                                  {user.displayName}
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      ) : (
                        <div>
                          {selectedAssignee || issue?.fields?.assignee ? (
                            // eslint-disable-next-line
                            <div
                              onClick={() => setIsAssigneeEditing(true)}
                              className="flex cursor-pointer items-center"
                            >
                              {avatarUrl && (
                                <img
                                  src={avatarUrl}
                                  alt={
                                    (selectedAssignee || issue.fields.assignee)
                                      .displayName
                                  }
                                  className="mr-2 h-7 w-7 rounded-full"
                                />
                              )}
                              {
                                (selectedAssignee || issue.fields.assignee)
                                  .displayName
                              }
                            </div>
                          ) : (
                            <span className="italic text-gray-500">
                              Unassigned
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="grid grid-cols-2 items-center py-2 ">
                    <div className="flex items-center">
                      <span className="ml-2">Labels</span>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {issue?.fields?.labels &&
                      issue?.fields?.labels.length > 0 ? (
                        issue?.fields?.labels.map((label) => (
                          <span
                            key={label}
                            className="rounded-lg bg-zinc-300  px-2 py-1 text-xs font-medium text-gray-600"
                          >
                            {label}
                          </span>
                        ))
                      ) : (
                        <span className="rounded-lg bg-gray-400 px-3 py-1 text-sm font-medium text-white">
                          None
                        </span>
                      )}
                    </div>
                  </div>
                  {!(
                    issue?.fields?.issuetype?.name?.toLowerCase() === 'epic'
                  ) && (
                    <div className="grid grid-cols-2 items-center py-2 ">
                      <div className="flex items-center">
                        <span className="ml-2">Parent</span>
                      </div>
                      {loading ? (
                        <div className="h-6 w-full animate-pulse rounded bg-gray-300" />
                      ) : isEditing ? (
                        <div className="relative w-full">
                          <input
                            type="text"
                            name="parentKey"
                            value={selectedParentKey}
                            onChange={(e) => {
                              setSelectedParentKey(e.target.value);
                              setShowDropdown(true);
                            }}
                            autoComplete="off"
                            onFocus={() => setShowDropdown(true)}
                            onBlur={() =>
                              setTimeout(() => setShowDropdown(false), 200)
                            }
                            className="w-full rounded-md border border-gray-300 p-1 text-sm font-semibold text-gray-800 focus:outline-none focus:ring-0"
                          />

                          {showDropdown && filteredIssues.length > 0 && (
                            <ul className="scrollbar-thin scrollbar-track-gray-200 scrollbar-thumb-gray-500 absolute left-0 z-10 max-h-40 w-full overflow-y-auto rounded border bg-white shadow-md ">
                              {filteredIssues.map((issueItem) => (
                                // eslint-disable-next-line
                                <li
                                  key={issueItem.key}
                                  className="flex cursor-pointer items-center p-2 text-sm hover:bg-gray-200"
                                  onMouseDown={(e) => {
                                    setSelectedParentKey(issueItem.key);
                                    setShowDropdown(false);

                                    setUpdatedIssue((prevIssue) => ({
                                      ...prevIssue,
                                      fields: {
                                        ...prevIssue.fields,
                                        parent: {
                                          ...prevIssue.fields?.parent,
                                          key: issueItem.key, // Update the parent key inside fields
                                        },
                                      },
                                    }));
                                  }}
                                >
                                  {
                                    <img
                                      key={issueItem?.issuetype}
                                      src={`/assets/jiraissuetable/jiraIssueTypeIcons/${issueItem?.issuetype?.toLowerCase()}.png`}
                                      alt={`${issueItem?.issuetype} Icon`}
                                      className="mr-1 h-4 w-4"
                                      onError={(e) =>
                                        (e.target.style.display = 'none')
                                      }
                                    />
                                  }
                                  {issueItem.key}
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      ) : (
                        <>
                          <div>
                            {issue?.fields?.parent?.key || (
                              <span className="italic text-gray-500">None</span>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  )}

                  {/* Due Date */}
                  <div className="grid grid-cols-2 items-center py-2">
                    <div className="flex items-center">
                      <span className="ml-2">Due Date</span>
                    </div>
                    <div>
                      {isEditing ? (
                        <input
                          type="date"
                          name="duedate"
                          value={updatedIssue?.fields?.duedate || ''}
                          onChange={handleInputChange}
                          className="rounded-md border border-gray-300 p-2 focus:outline-none focus:ring-0"
                        />
                      ) : issue?.fields?.duedate ? (
                        convertToIST(issue.fields.duedate)
                      ) : (
                        <span className="italic text-gray-500">None</span>
                      )}
                    </div>
                  </div>

                  {/* Reporter */}
                  <div className="grid grid-cols-2 items-center py-2 ">
                    <div className="flex items-center">
                      <span className="ml-2">Reporter</span>
                    </div>
                    <div>
                      {issue.fields?.reporter ? (
                        <div className="flex items-center">
                          <FaUserCircle className="mr-2 text-green-500" />
                          {issue.fields?.reporter?.displayName}
                        </div>
                      ) : (
                        <span className="italic text-gray-500">
                          No Reporter
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Automation Section */}
            <div className="mt-4">
              {/* eslint-disable-next-line */}
              <div
                className="flex cursor-pointer items-center justify-between border border-gray-300 px-4 py-2"
                onClick={toggleAutomation}
              >
                <h4 className="font-medium text-gray-700">Automation</h4>
                <button className="text-gray-500">
                  {isAutomationOpen ? <FaChevronUp /> : <FaChevronDown />}
                </button>
              </div>
              {isAutomationOpen && (
                <div className="space-y-2 border  border-t-0 border-gray-300 px-4 py-2 text-gray-700">
                  <div className="flex items-center">
                    <FaRocket className="text-gray-500" />
                    <span className="ml-2">Rule executions</span>
                  </div>
                  {/* Add more dummy fields here */}
                  <div className="flex items-center">
                    <FaRocket className="text-gray-500" />
                    <span className="ml-2">Dummy field 1</span>
                  </div>
                  <div className="flex items-center">
                    <FaRocket className="text-gray-500" />
                    <span className="ml-2">Dummy field 2</span>
                  </div>
                </div>
              )}
            </div>

            {/* Created and Updated Section */}
            <div className="mt-4 text-sm text-gray-600">
              <p>Created {new Date(issue?.fields?.created).toLocaleString()}</p>
              <p>Updated {new Date(issue?.fields?.updated).toLocaleString()}</p>
            </div>
          </div>
        </div>

        {isEditing && (
          <div className="mt-4 flex justify-end gap-4 text-sm">
            <button
              className="rounded-md bg-gray-300 px-2 py-1 text-gray-700 hover:bg-gray-400"
              onClick={() => setIsEditing(false)}
            >
              Cancel
            </button>
            <button
              disabled={saveLoading}
              className="rounded-md bg-[#5652CF] px-2 py-1 text-white hover:bg-blue-700"
              onClick={handleUpdateIssue}
            >
              {saveLoading ? 'Saving...' : 'Save'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default JiraIssueModal;
